import {
  LatestProductVersionsListQueryVariables,
  ProductLatestVersionDataFragment,
  ProductLatestVersionListDataFragment,
  ProductSoftwareCatalogDataFragment,
  ProductSoftwareCatalogQueryVariables,
  ProductSoftwareEntryDataFragment,
  ProductVersionsDataFragment,
  ProductVersionsQueryVariables,
  useLatestProductVersionsListQuery,
  useProductSoftwareCatalogQuery,
  useProductVersionsQuery
} from "generated/graphql";
import { getProductName } from "utilities/licenseServerUtils";
import { parseIntSecondsTimeMilliseconds } from "utilities/datetime";
import * as Urql from "polygraphql";
import { useMemo } from "react";

export type ProductSoftwareEntry = Omit<
  ProductSoftwareEntryDataFragment,
  "__typename" | "fileSize" | "lastModifiedTime"
> & {
  fileSize: number;
  lastModifiedTime: number;
};

export type ProductSoftwareCatalog = Omit<
  ProductSoftwareCatalogDataFragment,
  "__typename" | "softwareArtifacts"
> & {
  softwareArtifacts: Array<ProductSoftwareEntry>;
};

export type ProductLatestVersion = Omit<
  ProductLatestVersionDataFragment,
  "__typename"
> & {
  name: string | undefined;
};

export type ProductLatestVersionList = Omit<
  ProductLatestVersionListDataFragment,
  "__typename" | "latestVersions"
> & {
  latestVersions: Array<ProductLatestVersion>;
};

export type ProductVersions = Omit<
  ProductVersionsDataFragment,
  "__typename"
> & {
  name: string | undefined;
  latestVersion: string | undefined;
};

export const toProductSoftwareEntry = (
  data: ProductSoftwareEntryDataFragment
): ProductSoftwareEntry => {
  return {
    artifactName: data.artifactName,
    fileSize: parseInt(data.fileSize),
    lastModifiedTime: parseIntSecondsTimeMilliseconds(data.lastModifiedTime),
    url: data.url
  };
};

export const toProductSoftwareCatalog = (
  data: ProductSoftwareCatalogDataFragment
): ProductSoftwareCatalog => {
  return {
    productId: data.productId,
    version: data.version,
    softwareArtifacts: data.softwareArtifacts.map(entry =>
      toProductSoftwareEntry(entry)
    )
  };
};

export const toProductLatestVersion = (
  data: ProductLatestVersionDataFragment
): ProductLatestVersion => {
  return {
    ...data,
    name: getProductName(data.productId)
  };
};

export const toProductLatestVersionList = (
  data: ProductLatestVersionListDataFragment
): ProductLatestVersionList => {
  return {
    latestVersions: data.latestVersions.map(entry =>
      toProductLatestVersion(entry)
    )
  };
};

export const toProductVersions = (
  data: ProductVersionsDataFragment
): ProductVersions => {
  const productVersions = data.versions;
  const latestProductVersion =
    productVersions.length > 0
      ? productVersions[productVersions.length - 1]
      : undefined;

  return {
    ...data,
    name: getProductName(data.productId),
    latestVersion: latestProductVersion
  };
};

export type GetLatestProductVersionsListOptions = Omit<
  Urql.UseQueryArgs<LatestProductVersionsListQueryVariables>,
  "query"
>;

export const useGetLatestProductVersionsList = (
  options: GetLatestProductVersionsListOptions = {}
) => {
  const queryResults = useLatestProductVersionsListQuery({
    ...options
  });

  const data = useMemo(() => {
    let result;
    const sourceData = queryResults.data?.getLatestProductVersions;
    if (sourceData) {
      result = toProductLatestVersionList(sourceData);
    }
    return result;
  }, [queryResults.data]);

  return { ...queryResults, data };
};

export type GetProductSoftwareCatalogOptions = Omit<
  Urql.UseQueryArgs<ProductSoftwareCatalogQueryVariables>,
  "query"
>;

export const useGetProductSoftwareCatalog = (
  options: GetProductSoftwareCatalogOptions = {}
) => {
  const queryResults = useProductSoftwareCatalogQuery({
    ...options
  });

  const data = useMemo(() => {
    let result;
    const sourceData = queryResults.data?.getProductSoftwareCatalog;
    if (sourceData) {
      result = toProductSoftwareCatalog(sourceData);
    }
    return result;
  }, [queryResults.data]);

  return { ...queryResults, data };
};

export type GetProductVersionsOptions = Omit<
  Urql.UseQueryArgs<ProductVersionsQueryVariables>,
  "query"
>;

export const useGetProductVersions = (
  options: GetProductVersionsOptions = {}
) => {
  const queryResults = useProductVersionsQuery({
    ...options
  });

  const data = useMemo(() => {
    let result;
    const sourceData = queryResults.data?.getProductVersions;
    if (sourceData) {
      result = toProductVersions(sourceData);
    }
    return result;
  }, [queryResults.data]);

  return { ...queryResults, data };
};
